import { Link } from "react-router-dom";
import PageContainer, { PageHeader } from "@/components/PageContainer";
import { Check, ArrowRight } from "lucide-react";

const DIFFS = [
  {
    label: "vs. DIY",
    title: "You have 19 frameworks and no assignment.",
    detail:
      "Reading nine white papers is not an operating system. The Continuum is documented, role-assigned, and QA-audited.",
  },
  {
    label: "vs. Big-4 consultants",
    title: "You get slide decks. You do not get workflows.",
    detail:
      "This is built by a working LPC with a CCO track record — every artifact was shipped into a real behavioral health operation before it entered the playbook.",
  },
  {
    label: "vs. point tools",
    title: "Kipu manages notes. Sigmund manages billing.",
    detail:
      "Neither manages the clinical operating system that decides what a note should say, or which framework a therapist should be practicing this month.",
  },
];

const PROOF = [
  { k: "19", v: "Integrated frameworks", n: "9 core + 10 supplemental" },
  { k: "6", v: "Levels of care", n: "NARR I–IV + ASAM 1.0–4.0" },
  { k: "10+", v: "Role playbooks", n: "Reception through executive" },
  { k: "152", v: "Master playbook pages", n: "Print-ready" },
  { k: "1", v: "Live portal", n: "This one" },
  { k: "10+", v: "Interactive tools", n: "Scorers, audits, MBC" },
];

const PROBLEMS = [
  {
    n: "01",
    title: "Fragmented care levels",
    detail: "Each program runs its own workflows, uses its own language, measures its own outcomes.",
    solution: "One shared operating system. Assigned frameworks. Warm handoffs.",
  },
  {
    n: "02",
    title: "Framework overload",
    detail: "Nine papers on the shelf, none of them in tomorrow's clinical supervision.",
    solution: "Role portfolios — two to four frameworks per role, each with a specific job.",
  },
  {
    n: "03",
    title: "Documentation as compliance chore",
    detail: "Notes written to satisfy a payer, not to move the patient forward.",
    solution: "Documentation as a clinical act. If a payer requirement conflicts with clinical care, we document why.",
  },
];

export default function Positioning() {
  return (
    <PageContainer testid="positioning-page">
      <PageHeader
        overline="Positioning"
        title="The only integrated 19-framework operational stack for behavioral health."
        subtitle="From ASAM Level 3.1 recovery housing through Level 4 medically managed inpatient. Documented, role-assigned, and QA-audited."
        actions={
          <Link to="/book-call" className="btn btn-primary" data-testid="positioning-book-cta">
            Book a discovery call <ArrowRight size={16} strokeWidth={2} />
          </Link>
        }
      />

      <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-14" data-testid="positioning-proof">
        {PROOF.map((p) => (
          <div key={p.v} className="card p-4">
            <div className="text-2xl font-semibold text-navy">{p.k}</div>
            <div className="text-sm text-ink mt-0.5">{p.v}</div>
            <div className="text-xs text-ink-muted mt-0.5">{p.n}</div>
          </div>
        ))}
      </section>

      <section className="mb-14" data-testid="positioning-problems">
        <div className="overline mb-2">Three problems it solves</div>
        <h2 className="text-navy text-2xl font-semibold tracking-tightest mb-6">
          Where behavioral health operations tend to break.
        </h2>
        <div className="grid lg:grid-cols-3 gap-4">
          {PROBLEMS.map((p) => (
            <article key={p.n} className="card p-6">
              <div className="text-teal text-sm font-semibold mb-2">{p.n}</div>
              <h3 className="text-navy font-semibold mb-2">{p.title}</h3>
              <p className="text-sm text-ink-muted mb-4 leading-relaxed">{p.detail}</p>
              <div className="rule mb-3" />
              <p className="text-sm text-navy leading-relaxed">{p.solution}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="mb-14" data-testid="positioning-differentiators">
        <div className="overline mb-2">Differentiators</div>
        <h2 className="text-navy text-2xl font-semibold tracking-tightest mb-6">
          Why this exists — and what it is not.
        </h2>
        <div className="grid lg:grid-cols-3 gap-4">
          {DIFFS.map((d) => (
            <article key={d.label} className="card p-6">
              <div className="badge badge-rust mb-3">{d.label}</div>
              <h3 className="text-navy font-semibold mb-2">{d.title}</h3>
              <p className="text-sm text-ink-muted leading-relaxed">{d.detail}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="card p-8 lg:p-10 bg-teal-light/40 border-teal/30" data-testid="positioning-who-for">
        <div className="overline mb-3">Who it's for</div>
        <h2 className="text-navy text-2xl font-semibold tracking-tightest mb-4">
          Behavioral health operators who need a shared operating system.
        </h2>
        <ul className="grid md:grid-cols-2 gap-x-6 gap-y-3">
          {[
            "New or scaling BH operators 30–90 days from opening or first survey",
            "CEOs, CCOs, and Medical Directors of existing operators with fragmented levels",
            "PE-backed BH platforms rolling up multiple facilities",
            "Hospital systems adding a BH service line",
            "State agencies or Medicaid MCOs improving provider networks",
            "Individual clinicians and small NARR homes wanting Level III/IV certification",
          ].map((item) => (
            <li key={item} className="flex gap-2 items-start text-sm text-navy">
              <Check size={16} strokeWidth={2.5} className="text-teal mt-0.5 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </section>
    </PageContainer>
  );
}
